import React, { createContext, PropsWithChildren, useContext, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { CollaboratorQueryKeys } from "@shared/libs/react-query/query-keys.constants";
import { useGetMe } from "hassibot/services_v2/collaborator/query/collaborator.queries";
import { SessionCollaborator } from "hassibot/services_v2/login/types";

type SessionCollaboratorContextValue = {
  sessionCollaborator: SessionCollaborator;
  handleSessionCollaboratorChange: (
    updater: (prev: SessionCollaborator) => SessionCollaborator
  ) => void;
};

const SessionCollaboratorContext = createContext<SessionCollaboratorContextValue | null>(null);

export const useSessionCollaboratorContext = (): SessionCollaboratorContextValue => {
  const realContext = useContext(SessionCollaboratorContext);
  if (realContext === null) {
    throw new Error("useSessionCollaboratorContext must be used within SessionContainer");
  }

  return realContext;
};

/**
 * This container fetches current user data.
 */
export const SessionContainer: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const queryClient = useQueryClient();

  // Store transformations per key to avoid overriding each other
  const transformationsRef = useRef<Partial<SessionCollaborator>>({});

  const { data: sessionCollaborator } = useGetMe([], {
    staleTime: 30 * 60 * 1000, // Cache for 30 minutes
    retry: 2,
    select: data => ({
      ...data,
      ...transformationsRef.current,
    }),
  });

  if (!sessionCollaborator) {
    return null;
  }

  const handleSessionCollaboratorChange = (
    updater: (prev: SessionCollaborator) => SessionCollaborator
  ) => {
    // Get current cache data
    const currentData = queryClient.getQueryData<SessionCollaborator>([
      CollaboratorQueryKeys.ME,
      [],
    ]);

    if (!currentData) return;

    // Apply the updater to get the new data
    const newData = updater(currentData);

    // Extract only the changed keys and update transformations
    Object.keys(newData).forEach(key => {
      if (newData[key] !== currentData[key]) {
        transformationsRef.current[key] = newData[key];
      }
    });

    // Apply immediately to current cache
    queryClient.setQueryData([CollaboratorQueryKeys.ME, []], newData);
  };

  return (
    <SessionCollaboratorContext.Provider
      value={{ sessionCollaborator, handleSessionCollaboratorChange }}
    >
      {children}
    </SessionCollaboratorContext.Provider>
  );
};
